/**
 * Dashboard Search Bar
 *
 * Search component for finding routes/pages by card name, route URL, or content.
 * Can be displayed in full mode (dashboard) or compact mode (TopBar).
 *
 * Features:
 * - Full-text search across page titles, descriptions, routes, and keywords
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Compact mode for TopBar integration (icon-only on mobile, expanded on desktop)
 * - Global search functionality from any page
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, PanelRight, LucideIcon } from 'lucide-react';
import { ROUTES, TAB_TO_ROUTE } from '@/app/routes';
import { cn } from '@/lib/utils';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';

/**
 * Props for DashboardSearchBar component
 */
export interface DashboardSearchBarProps {
  /** Compact mode for TopBar (smaller, icon-only on mobile) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Search result item representing a navigable route
 */
interface SearchResult {
  id: string;
  title: string;
  description: string;
  route: string;
  icon?: LucideIcon;
  matchedField: 'title' | 'description' | 'route';
  matchedText: string;
}

/**
 * Route configuration for searchable pages
 * Consolidates card configs, TAB_TO_ROUTE, and ROUTES into searchable items
 */
interface RouteConfig {
  id: string;
  title: string;
  description: string;
  route: string;
  keywords?: string[];
}

/**
 * Cache for searchable routes - initialized lazily to avoid circular dependency
 */
let _searchableRoutesCache: RouteConfig[] | null = null;

/**
 * Get all searchable routes in the application.
 * Returns cached routes if available, otherwise builds the list.
 *
 * NOTE: This is a function (not a constant) to avoid circular dependency
 * with ROUTES. ROUTES is defined in routes.tsx which imports components
 * that may import this search bar.
 */
function getSearchableRoutes(): RouteConfig[] {
  if (_searchableRoutesCache) return _searchableRoutesCache;

  _searchableRoutesCache = [
    // Dashboard Cards - Campaign Quick-Access
    {
      id: 'event-planning',
      title: 'Event Planning',
      description: 'Plan and schedule events on the 12-month annual calendar grid EPG',
      route: ROUTES.EPG,
      keywords: ['epg', 'calendar', 'events', 'schedule', 'planning'],
    },
    {
      id: 'organizations',
      title: 'Organizations',
      description: 'Browse and manage organizations from your Pipedrive CRM',
      route: ROUTES.ORGANIZATIONS,
      keywords: ['orgs', 'companies', 'pipedrive', 'crm', 'clients'],
    },

    // Dashboard Cards - Post-Campaign Execution
    {
      id: 'weekly-drop-report',
      title: 'Weekly Drop Report',
      description: 'View dropped organizations grouped by week with drop reasons and follow-up status',
      route: ROUTES.WEEKLY_DROP_REPORT,
      keywords: ['drops', 'reports', 'weekly', 'analytics'],
    },
    {
      id: 'weekly-call-status',
      title: 'Weekly Call Status',
      description: 'Track call activities by time period showing answered and not answered calls',
      route: ROUTES.WEEKLY_CALL_STATUS,
      keywords: ['calls', 'phone', 'status', 'tracking', 'activities'],
    },
    {
      id: 'follow-ups',
      title: 'Follow-Ups',
      description: 'Manage email follow-ups for your drops',
      route: ROUTES.DROP_FOLLOW_UPS,
      keywords: ['email', 'follow up', 'drops', 'tasks'],
    },

    // Dashboard Cards - Administrative
    {
      id: 'vibe-remote',
      title: 'Vibe Remote',
      description: 'Remote terminal sessions from any device',
      route: ROUTES.VIBE_REMOTE,
      keywords: ['remote', 'sessions', 'terminal', 'vibe'],
    },
    {
      id: 'cli-dashboard',
      title: 'CLI Dashboard',
      description: 'Monitor and manage terminal sessions in real-time',
      route: ROUTES.CLI_DASHBOARD,
      keywords: ['cli', 'terminal', 'monitor', 'sessions', 'ops'],
    },
    {
      id: 'field-management',
      title: 'Field Management',
      description: 'Manage Pipedrive field definitions and sync with latest data',
      route: ROUTES.FIELD_MANAGEMENT,
      keywords: ['pipedrive', 'fields', 'admin', 'definitions', 'sync'],
    },
    {
      id: 'pug-records',
      title: 'PUG Records',
      description: 'View and manage Pipedrive organization sync records with PUG integration',
      route: ROUTES.PUG_RECORDS,
      keywords: ['pug', 'sync', 'pipedrive', 'records', 'integration'],
    },
    {
      id: 'expenses',
      title: 'Expenses',
      description: 'Upload receipts with OCR allocate costs per person and export to CSV',
      route: ROUTES.EXPENSES,
      keywords: ['receipts', 'ocr', 'costs', 'csv', 'finance', 'money'],
    },
    {
      id: 'project-board',
      title: 'Project Board',
      description: 'Kanban-style project management with boards lists and cards Planka',
      route: ROUTES.PLANKA,
      keywords: ['kanban', 'planka', 'tasks', 'boards', 'lists', 'project'],
    },

    // Dashboard Cards - Client Management
    {
      id: 'client-timelines',
      title: 'Client Timelines',
      description: 'Create and manage enrollment timelines for client organizations',
      route: ROUTES.TIMELINES,
      keywords: ['timelines', 'enrollment', 'clients', 'schedule'],
    },
    {
      id: 'availability',
      title: 'Availability',
      description: 'Find meeting times across team groups with calendar availability',
      route: ROUTES.AVAILABILITY,
      keywords: ['meetings', 'calendar', 'schedule', 'team', 'availability'],
    },

    // Dashboard Cards - Sales Tools
    {
      id: 'flashcards',
      title: 'Flashcards',
      description: 'Study outreach scripts benefits terms and platform tips with flip and self-grading',
      route: ROUTES.FLASHCARDS,
      keywords: ['study', 'learning', 'scripts', 'tips', 'training'],
    },
    {
      id: 'notes',
      title: 'Notes',
      description: 'Organize notes documents and whiteboards in hierarchical notebooks',
      route: ROUTES.NOTES,
      keywords: ['notes', 'documents', 'notebooks', 'whiteboard', 'writing'],
    },
    {
      id: 'houston-calendar',
      title: 'Houston Calendar',
      description: '12-month marketing calendar for Houston events with group-based scheduling',
      route: ROUTES.CALENDAR,
      keywords: ['houston', 'marketing', 'calendar', 'events', 'scheduling'],
    },
    {
      id: 'hmc-events',
      title: 'HMC Events',
      description: 'Upload Houston Marketing Calendar files and view event reports',
      route: ROUTES.HMC_EVENTS,
      keywords: ['hmc', 'houston', 'marketing', 'upload', 'events', 'reports'],
    },
    {
      id: 'file-inbox',
      title: 'File Inbox',
      description: 'View and download email attachments from Gmail polling',
      route: ROUTES.FILE_INBOX,
      keywords: ['gmail', 'email', 'attachments', 'inbox', 'files', 'download'],
    },
    {
      id: 'usi-events',
      title: 'USI Events',
      description: 'Send postcard emails to Event Driven Drops contacts',
      route: ROUTES.USI_EVENTS,
      keywords: ['usi', 'postcard', 'email', 'drops', 'events'],
    },

    // Additional routes from TAB_TO_ROUTE
    {
      id: 'campaigns',
      title: 'Campaign Management',
      description: 'Manage marketing campaigns and track their progress',
      route: ROUTES.CAMPAIGNS,
      keywords: ['campaigns', 'marketing', 'management'],
    },
    {
      id: 'addresses',
      title: 'Address Management',
      description: 'Manage and verify physical addresses for organizations',
      route: ROUTES.ADDRESSES,
      keywords: ['addresses', 'locations', 'geocoding', 'verification'],
    },
    {
      id: 'routes',
      title: 'Route Planning',
      description: 'Plan and optimize delivery routes',
      route: ROUTES.ROUTES,
      keywords: ['routes', 'delivery', 'planning', 'optimization', 'driving'],
    },
    {
      id: 'review-routes',
      title: 'Review Routes',
      description: 'Review and approve planned delivery routes',
      route: ROUTES.REVIEW_ROUTES,
      keywords: ['review', 'routes', 'approval'],
    },
    {
      id: 'contacts',
      title: 'Contact Management',
      description: 'Manage contacts and their information',
      route: ROUTES.CONTACTS,
      keywords: ['contacts', 'people', 'management'],
    },
    {
      id: 'labels',
      title: 'Label Generator',
      description: 'Generate mailing labels for organizations',
      route: ROUTES.LABELS,
      keywords: ['labels', 'printing', 'mailing', 'generator'],
    },
    {
      id: 'execution',
      title: 'Execution Tracking',
      description: 'Track execution status of campaigns and drops',
      route: ROUTES.EXECUTION,
      keywords: ['execution', 'tracking', 'status', 'progress'],
    },
    {
      id: 'handwrytten',
      title: 'Handwrytten Export',
      description: 'Export data for Handwrytten card orders',
      route: ROUTES.HANDWRYTTEN,
      keywords: ['handwrytten', 'cards', 'export', 'handwritten'],
    },
    {
      id: 'report-generator',
      title: 'Report Generator',
      description: 'Generate OMNI underwriting reports for 15 DP opportunities',
      route: ROUTES.REPORT_GENERATOR,
      keywords: ['omni', 'underwriting', 'reports', '15dp', 'generator'],
    },
    {
      id: 'email-invitations',
      title: 'Email Invitations',
      description: 'Mass email campaign manager for cenari.net invitations',
      route: ROUTES.EMAIL_INVITATIONS,
      keywords: ['email', 'invitations', 'mass', 'cenari', 'campaign'],
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Central hub for managing campaigns routes and execution tracking',
      route: ROUTES.DASHBOARD,
      keywords: ['dashboard', 'home', 'hub', 'overview'],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'View all system notifications and alerts',
      route: ROUTES.NOTIFICATIONS,
      keywords: ['notifications', 'alerts', 'messages'],
    },
  ];

  return _searchableRoutesCache;
}

/**
 * Search function that finds matches in title, description, route, and keywords
 */
function searchRoutes(query: string): SearchResult[] {
  if (!query.trim()) return [];

  const normalizedQuery = query.toLowerCase().trim();
  const results: SearchResult[] = [];
  const searchableRoutes = getSearchableRoutes();

  for (const route of searchableRoutes) {
    // Check title match
    if (route.title.toLowerCase().includes(normalizedQuery)) {
      results.push({
        id: route.id,
        title: route.title,
        description: route.description,
        route: route.route,
        matchedField: 'title',
        matchedText: route.title,
      });
      continue;
    }

    // Check route URL match
    if (route.route.toLowerCase().includes(normalizedQuery)) {
      results.push({
        id: route.id,
        title: route.title,
        description: route.description,
        route: route.route,
        matchedField: 'route',
        matchedText: route.route,
      });
      continue;
    }

    // Check description match
    if (route.description.toLowerCase().includes(normalizedQuery)) {
      results.push({
        id: route.id,
        title: route.title,
        description: route.description,
        route: route.route,
        matchedField: 'description',
        matchedText: route.description,
      });
      continue;
    }

    // Check keywords match
    if (route.keywords?.some((kw) => kw.toLowerCase().includes(normalizedQuery))) {
      const matchedKeyword = route.keywords.find((kw) =>
        kw.toLowerCase().includes(normalizedQuery)
      );
      results.push({
        id: route.id,
        title: route.title,
        description: route.description,
        route: route.route,
        matchedField: 'description',
        matchedText: `Keyword: ${matchedKeyword}`,
      });
    }
  }

  // Sort results: title matches first, then route, then description
  return results.sort((a, b) => {
    const order = { title: 0, route: 1, description: 2 };
    return order[a.matchedField] - order[b.matchedField];
  });
}

/**
 * Highlight matching text in a string
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const normalizedQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();
  const matchIndex = lowerText.indexOf(normalizedQuery);

  if (matchIndex === -1) return text;

  const before = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + query.length);
  const after = text.slice(matchIndex + query.length);

  return (
    <>
      {before}
      <span className="bg-brand-500/30 text-brand-300 font-medium">{match}</span>
      {after}
    </>
  );
}

/**
 * Dashboard Search Bar Component
 */
export const DashboardSearchBar: React.FC<DashboardSearchBarProps> = ({
  compact = false,
  className = '',
}) => {
  const navigate = useNavigate();
  const openPanel = useAppPanelStore((s) => s.openPanel);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPanelFocused, setIsPanelFocused] = useState(false); // Track if "Open as Panel" is focused
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // In compact mode, expand on focus/click
  const handleSearchIconClick = useCallback(() => {
    if (compact) {
      setIsExpanded(true);
      // Focus input after expansion
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [compact]);

  // Listen for global 'focus-search-bar' event (triggered by 'S' keyboard shortcut)
  useEffect(() => {
    const handleFocusSearchBar = () => {
      if (compact) {
        setIsExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      } else {
        inputRef.current?.focus();
      }
    };

    window.addEventListener('focus-search-bar', handleFocusSearchBar);
    return () => window.removeEventListener('focus-search-bar', handleFocusSearchBar);
  }, [compact]);

  // Collapse in compact mode when clicking outside
  useEffect(() => {
    if (!compact || !isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!query.trim()) {
          setIsExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [compact, isExpanded, query]);

  // Search results
  const results = useMemo(() => searchRoutes(query), [query]);

  // Reset selected index and panel focus when results change
  useEffect(() => {
    setSelectedIndex(0);
    setIsPanelFocused(false);
  }, [results]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) {
        if (e.key === 'Enter' && query.trim()) {
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setIsPanelFocused(false); // Reset panel focus when changing rows
          setSelectedIndex((prev) => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setIsPanelFocused(false); // Reset panel focus when changing rows
          setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
          break;
        case 'Tab':
          // Tab toggles between Navigate and Open as Panel
          e.preventDefault();
          setIsPanelFocused((prev) => !prev);
          break;
        case 'ArrowRight':
          // Right arrow also moves to panel button
          if (!isPanelFocused) {
            e.preventDefault();
            setIsPanelFocused(true);
          }
          break;
        case 'ArrowLeft':
          // Left arrow moves back from panel button
          if (isPanelFocused) {
            e.preventDefault();
            setIsPanelFocused(false);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            const result = results[selectedIndex];
            if (isPanelFocused) {
              // Open as panel
              openPanel(result.route, result.title);
            } else {
              // Navigate normally
              navigate(result.route);
            }
            setQuery('');
            setIsOpen(false);
            setIsPanelFocused(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setIsPanelFocused(false);
          break;
      }
    },
    [isOpen, results, selectedIndex, navigate, openPanel, query, isPanelFocused]
  );

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
  }, []);

  // Handle result click (navigate)
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      navigate(result.route);
      setQuery('');
      setIsOpen(false);
      setIsPanelFocused(false);
    },
    [navigate]
  );

  // Handle open as panel click
  const handleOpenAsPanel = useCallback(
    (result: SearchResult, e: React.MouseEvent) => {
      e.stopPropagation();
      openPanel(result.route, result.title);
      setQuery('');
      setIsOpen(false);
      setIsPanelFocused(false);
    },
    [openPanel]
  );

  // Clear search
  const handleClear = useCallback(() => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  // Compact mode: Show icon button on mobile, expanded input on desktop or when active
  if (compact) {
    return (
      <div
        ref={containerRef}
        className={cn('relative', className)}
        data-testid="dashboard-search-compact"
      >
        {/* Mobile: Icon-only button */}
        {!isExpanded && (
          <button
            onClick={handleSearchIconClick}
            className="sm:hidden p-2 text-dark-muted hover:text-dark-text hover:bg-dark-surface rounded-lg transition-all duration-150"
            aria-label="Open search"
          >
            <Search size={16} />
          </button>
        )}

        {/* Desktop: Always show input | Mobile: Show when expanded */}
        <div className={cn(
          'relative',
          !isExpanded && 'hidden sm:block'
        )}>
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-dark-muted"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.trim() && setIsOpen(true)}
            placeholder="Search..."
            className={cn(
              'w-full pl-8 pr-8 py-1.5 text-xs rounded-md',
              'bg-dark-surface border border-dark-border/30',
              'text-dark-text placeholder:text-dark-muted',
              'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/50 focus:border-brand-500',
              'transition-all duration-200'
            )}
            data-testid="dashboard-search-input-compact"
          />
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Search Results Dropdown - Compact */}
        {isOpen && results.length > 0 && (isExpanded || true) && (
          <div
            className={cn(
              'absolute z-50 w-72 mt-1 py-2 rounded-lg',
              'bg-dark-surface border border-dark-border/30',
              'shadow-xl max-h-[280px] overflow-y-auto',
              'left-0 sm:left-auto sm:right-0'
            )}
            data-testid="dashboard-search-results-compact"
          >
            {results.slice(0, 8).map((result, index) => (
              <div
                key={result.id}
                onMouseEnter={() => {
                  setSelectedIndex(index);
                  setIsPanelFocused(false);
                }}
                className={cn(
                  'w-full px-3 py-2 flex items-center gap-2',
                  'transition-colors duration-100',
                  index === selectedIndex
                    ? 'bg-dark-surface/50'
                    : 'hover:bg-dark-surface/40/50'
                )}
                data-testid={`search-result-compact-${result.id}`}
              >
                {/* Main clickable area - Navigate */}
                <button
                  onClick={() => handleResultClick(result)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="text-sm font-medium text-dark-text truncate">
                    {result.title}
                  </div>
                  <div className="text-xs text-dark-muted truncate">
                    {result.description}
                  </div>
                </button>

                {/* Open as Panel button */}
                <button
                  onClick={(e) => handleOpenAsPanel(result, e)}
                  onMouseEnter={() => setIsPanelFocused(true)}
                  onMouseLeave={() => setIsPanelFocused(false)}
                  className={cn(
                    'shrink-0 p-1.5 rounded-md transition-all',
                    index === selectedIndex && isPanelFocused
                      ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/50'
                      : index === selectedIndex
                        ? 'text-dark-muted hover:text-brand-400 hover:bg-brand-500/10'
                        : 'opacity-0'
                  )}
                  title="Open as Panel (Tab to select)"
                  aria-label={`Open ${result.title} as panel`}
                >
                  <PanelRight size={14} />
                </button>

                {/* Navigate arrow */}
                <ArrowRight
                  size={14}
                  className={cn(
                    'shrink-0 transition-all',
                    index === selectedIndex && !isPanelFocused
                      ? 'opacity-100 text-brand-400'
                      : 'opacity-0'
                  )}
                />
              </div>
            ))}
          </div>
        )}

        {/* No Results - Compact */}
        {isOpen && query.trim() && results.length === 0 && (
          <div
            className={cn(
              'absolute z-50 w-72 mt-1 py-3 px-3 rounded-lg',
              'bg-dark-surface border border-dark-border/30',
              'shadow-xl text-center',
              'left-0 sm:left-auto sm:right-0'
            )}
          >
            <p className="text-dark-muted text-xs">
              No pages found for "{query}"
            </p>
          </div>
        )}
      </div>
    );
  }

  // Full mode (default)
  return (
    <div
      ref={containerRef}
      className={cn('w-full max-w-xl mx-auto relative', className)}
      data-testid="dashboard-search"
    >
      {/* Search Input */}
      <div className="relative">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setIsOpen(true)}
          placeholder="Search pages by name, URL, or content..."
          className={cn(
            'w-full pl-10 pr-10 py-2.5 rounded-lg',
            'bg-dark-surface border border-dark-border/30',
            'text-dark-text placeholder:text-dark-muted',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/50 focus:border-brand-500',
            'transition-all duration-200'
          )}
          data-testid="dashboard-search-input"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark-text p-0.5"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          className={cn(
            'absolute z-50 w-full mt-2 py-2 rounded-lg',
            'bg-dark-surface border border-dark-border/30',
            'shadow-xl max-h-[320px] overflow-y-auto'
          )}
          data-testid="dashboard-search-results"
        >
          {results.map((result, index) => (
            <div
              key={result.id}
              onMouseEnter={() => {
                setSelectedIndex(index);
                setIsPanelFocused(false);
              }}
              className={cn(
                'w-full px-4 py-3 flex items-center gap-3',
                'transition-colors duration-100',
                index === selectedIndex
                  ? 'bg-dark-surface/50'
                  : 'hover:bg-dark-surface/40/50'
              )}
              data-testid={`search-result-${result.id}`}
            >
              {/* Main clickable area - Navigate */}
              <button
                onClick={() => handleResultClick(result)}
                className="flex-1 min-w-0 text-left"
              >
                {/* Page Title */}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-dark-text">
                    {result.matchedField === 'title'
                      ? highlightMatch(result.title, query)
                      : result.title}
                  </span>
                </div>

                {/* Matching Context */}
                <div className="text-sm text-dark-muted mt-0.5 truncate">
                  {result.matchedField === 'route' && (
                    <span className="text-brand-400 font-mono text-xs">
                      {highlightMatch(result.route, query)}
                    </span>
                  )}
                  {result.matchedField === 'description' && (
                    <span>{highlightMatch(result.description, query)}</span>
                  )}
                  {result.matchedField === 'title' && (
                    <span className="text-dark-muted/70">{result.description}</span>
                  )}
                </div>
              </button>

              {/* Open as Panel button */}
              <button
                onClick={(e) => handleOpenAsPanel(result, e)}
                onMouseEnter={() => setIsPanelFocused(true)}
                onMouseLeave={() => setIsPanelFocused(false)}
                className={cn(
                  'shrink-0 p-2 rounded-md transition-all',
                  index === selectedIndex && isPanelFocused
                    ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/50'
                    : index === selectedIndex
                      ? 'text-dark-muted hover:text-brand-400 hover:bg-brand-500/10'
                      : 'opacity-0'
                )}
                title="Open as Panel (Tab to select)"
                aria-label={`Open ${result.title} as panel`}
              >
                <PanelRight size={16} />
              </button>

              {/* Navigate arrow */}
              <ArrowRight
                size={16}
                className={cn(
                  'shrink-0 transition-all',
                  index === selectedIndex && !isPanelFocused
                    ? 'opacity-100 text-brand-400'
                    : 'opacity-0'
                )}
              />
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {isOpen && query.trim() && results.length === 0 && (
        <div
          className={cn(
            'absolute z-50 w-full mt-2 py-4 px-4 rounded-lg',
            'bg-dark-surface border border-dark-border/30',
            'shadow-xl text-center'
          )}
        >
          <p className="text-dark-muted text-sm">
            No pages found for "{query}"
          </p>
        </div>
      )}
    </div>
  );
};
